import { useEffect, useRef, useState } from 'react';
import { JSONObject } from '../types/types';
import { resolveStringExpression } from '../utils/json/resolveStringExpression';
import { debugLogger } from '../debug/DebugLogger';

const log = debugLogger.scope('visibility');

const RESOLVE_DEBOUNCE_MS = 250;

export const getInitialVisibility = (visible?: boolean | string): boolean => {
  if (typeof visible === 'string') {
    return false;
  }
  if (typeof visible === 'boolean') {
    return visible;
  }
  return true;
};

export const useVisibilityResolve = (
  visible: boolean | string | undefined,
  visibilityData: JSONObject,
  // Field/group identity for debug logs only — logs are anonymous without it.
  debugId?: string
) => {
  const [resolvedVisible, setResolvedVisible] = useState<boolean>(() =>
    getInitialVisibility(visible)
  );
  const [pending, setPending] = useState(() => typeof visible === 'string');
  const isFirstRun = useRef(true);
  const lastLoggedRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    if (typeof visible !== 'string') {
      setResolvedVisible(getInitialVisibility(visible));
      setPending(false);
      return;
    }

    // Ignore results from stale in-flight requests: when `visible` or `visibilityData`
    // changes (or the component unmounts), a previous async resolution may finish later
    // and would overwrite state with the wrong outcome if we still called setVisibility.
    let cancelled = false;
    setPending(true);

    const delay = isFirstRun.current ? 0 : RESOLVE_DEBOUNCE_MS;
    isFirstRun.current = false;

    const timeoutId = setTimeout(() => {
      resolveStringExpression(visible, visibilityData)
        .then((resolved: boolean) => {
          if (!cancelled) {
            const value = Boolean(resolved);
            if (lastLoggedRef.current !== value) {
              lastLoggedRef.current = value;
              /** @debugFeature visibility Logs a resolved `visible` expression outcome for a form field/group — only when the value changed. */
              log('visible resolved', () => ({
                debugId,
                expression: visible,
                visible: value,
              }));
            }
            setResolvedVisible(value);
          }
        })
        .catch((error) => {
          if (!cancelled) {
            console.error(
              '[useVisibilityResolve] Failed to evaluate `visible` JSONata expression.',
              { expression: visible, error }
            );

            /** @debugFeature visibility Logs a failed `visible` expression — the field/group defaults to hidden. */
            log('visible expression failed, defaulting to hidden', () => ({
              debugId,
              expression: visible,
              error,
            }));

            lastLoggedRef.current = false;
            setResolvedVisible(false);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setPending(false);
          }
        });
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [visible, visibilityData]);

  return { visible: resolvedVisible, pending };
};
